import React, { useState, useEffect, useRef } from 'react';
import { MapPinned, LogIn, UserPlus, Sparkles, Layers3, Sofa, PenTool, Globe, Film, Video, Scissors, Monitor, Clapperboard, Wrench, LogOut, Coins, Loader2, FolderOpen, ChevronDown, Settings, Users, Shield, Check, Link2, LayoutDashboard, LayoutGrid, X, Zap, Tag, Gift, MoonStar, SlidersHorizontal, Receipt, CircleDot, FileText, FilePen } from 'lucide-react';
import { PLANOS_TAGLINE, PLANOS_FEATURES } from './constants';
import { CREDIT_EXPIRY_COPY } from '@rehub/pricing';
import { supabase, isConfigured, getProjects, createProject, deleteProject, openBillingPortal } from './services/supabase';
import { canUserPublish } from './services/hosting';
import type { Session } from '@supabase/supabase-js';
import type { UserProfile, PlanosProject, ViewMode } from './types';
import FluidBackground from './components/FluidBackground';
import Footer from './components/Footer';
import Login from './components/Login';
import PlanosEditor from './components/PlanosEditor';
import PlanosViewer from './components/PlanosViewer';
import Dashboard from './components/Dashboard';
import CreditsModal from './components/CreditsModal';
import SubscriptionPlansModal from './components/SubscriptionPlansModal';
import PricingBenefitsModal from './components/PricingBenefitsModal';
import ReferralsPage from './components/ReferralsPage';
import { ReferralPromoPopup } from '@rehub/announcements';

const HyperDuskSwitcherIcon = ({ size = 18, className }: { size?: string | number; className?: string }) => (
  <MoonStar size={Number(size)} className={className} strokeWidth={1.8} />
);

// Apps grouped the way the Hub buckets them, so the switcher gives context.
// Each group carries an accent colour + gradient used to theme its section.
const APP_GROUPS = [
  {
    id: 'workspace', label: 'Workspace',
    accent: 'text-purple-300', iconBg: 'bg-purple-500/15 text-purple-300', gradient: 'from-purple-500/20',
    apps: [
      { id: 'hub', label: 'Hub', icon: LayoutDashboard, url: 'https://hub.rehub.studio' },
      { id: 'studio', label: 'Studio', icon: Clapperboard, url: 'https://studio.rehub.studio' },
    ],
  },
  {
    id: 'photo', label: 'AI Photo',
    accent: 'text-sky-300', iconBg: 'bg-sky-500/15 text-sky-300', gradient: 'from-sky-500/20',
    apps: [
      { id: 'photoclear', label: 'Photoclear', icon: Sparkles, url: 'https://photoclear.rehub.studio' },
      { id: 'hdrphoto', label: 'HDR Photo', icon: Layers3, url: 'https://hdrphoto.rehub.studio' },
      { id: 'photoedit', label: 'PhotoEdit', icon: SlidersHorizontal, url: 'https://photoedit.rehub.studio' },
      { id: 'staging', label: 'Staging', icon: Sofa, url: 'https://staging.rehub.studio' },
    ],
  },
  {
    id: 'video', label: 'AI Video',
    accent: 'text-fuchsia-300', iconBg: 'bg-fuchsia-500/15 text-fuchsia-300', gradient: 'from-fuchsia-500/20',
    apps: [
      { id: 'imagemotion', label: 'Imagemotion', icon: Film, url: 'https://imagemotion.rehub.studio' },
      { id: 'videomotion', label: 'Videomotion', icon: Video, url: 'https://videomotion.rehub.studio' },
      { id: 'propertycut', label: 'PropertyCuts', icon: Scissors, url: 'https://propertycuts.rehub.studio' },
      { id: 'hyperdusk', label: 'Hyper Dusk', icon: HyperDuskSwitcherIcon, url: 'https://hyperdusk.rehub.studio' },
      { id: 'record', label: 'Screen Record', icon: CircleDot, url: 'https://record.rehub.studio' },
    ],
  },
  {
    id: 'tours', label: 'Tours & Floor Plans',
    accent: 'text-emerald-300', iconBg: 'bg-emerald-500/15 text-emerald-300', gradient: 'from-emerald-500/20',
    apps: [
      { id: 'tours', label: '360 Tours', icon: Globe, url: 'https://360tours.rehub.studio' },
      { id: 'planos', label: 'Planos', icon: MapPinned, url: 'https://planos.rehub.studio' },
      { id: 'sketchplan', label: 'Sketchplan', icon: PenTool, url: 'https://sketchplan.rehub.studio' },
    ],
  },
  {
    id: 'present', label: 'Presentations',
    accent: 'text-amber-300', iconBg: 'bg-amber-500/15 text-amber-300', gradient: 'from-amber-500/20',
    apps: [
      { id: 'showcase', label: 'Showcase', icon: Monitor, url: 'https://showcase.rehub.studio' },
      { id: 'brochures', label: 'BrochurePDF', icon: FileText, url: 'https://brochures.rehub.studio' },
      { id: 'pdf', label: 'ReHub PDF', icon: FilePen, url: 'https://pdf.rehub.studio' },
    ],
  },
  {
    id: 'tools', label: 'Free Tools',
    accent: 'text-slate-300', iconBg: 'bg-slate-500/15 text-slate-300', gradient: 'from-slate-500/20',
    apps: [
      { id: 'tools', label: 'Tools', icon: Wrench, url: 'https://tools.rehub.studio' },
    ],
  },
] as const;

const APPS = APP_GROUPS.flatMap((g) => g.apps as readonly (typeof APP_GROUPS)[number]['apps'][number][]);
type AppId = typeof APP_GROUPS[number]['apps'][number]['id'];

const handleAppNavigation = async (targetApp: AppId, url: string, e: React.MouseEvent) => {
  if (targetApp === 'planos') { e.preventDefault(); return; }
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token && session?.refresh_token) {
      e.preventDefault();
      window.location.href = `${url}#sso_access_token=${session.access_token}&sso_refresh_token=${session.refresh_token}`;
    }
  } catch { /* fallback */ }
};

/* ─── Header (same structure as tours, planos colours) ─────────────────────────────────────────── */
interface HeaderProps {
  user: UserProfile | null;
  onLogin: () => void;
  onLogout: () => void;
  onSignUp?: () => void;
  showBackToProjects?: boolean;
  onBackToProjects?: () => void;
  onOpenCredits?: () => void;
  onOpenPlans?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToMembers?: () => void;
  onNavigateToReferrals?: () => void;
  isAdmin?: boolean;
  onOpenAdmin?: () => void;
}

function formatCredits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
}

const LOGO_360_URL = 'https://360tours.rehub.studio/360tours-studio-logo-transparent.png';
const LOGO_PLANOS_URL = 'https://planos.rehub.studio/Planos%20Logo%20Red.png';

const Header: React.FC<HeaderProps> = ({ user, onLogin, onLogout, onSignUp, showBackToProjects, onBackToProjects, onOpenCredits, onOpenPlans, onNavigateToSettings, onNavigateToMembers, onNavigateToReferrals, isAdmin, onOpenAdmin }) => {
  const [accountOpen, setAccountOpen] = useState(false);
  const [creditsPanelOpen, setCreditsPanelOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const creditsRef = useRef<HTMLDivElement>(null);
  const appsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) setAccountOpen(false);
      if (creditsRef.current && !creditsRef.current.contains(e.target as Node)) setCreditsPanelOpen(false);
      if (appsRef.current && !appsRef.current.contains(e.target as Node)) setAppsOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Billing] portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <header className="sticky top-0 left-0 right-0 bg-black border-b border-slate-900 shrink-0 z-40 flex flex-col md:flex-row md:items-center md:h-16 px-4 sm:px-6">
      <div className="flex flex-col md:flex-row md:flex-1 md:items-center md:justify-between">
        <div className="flex items-center justify-between w-full min-h-14 md:min-h-0 py-3 md:py-0 gap-3">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <a href="/" className="flex items-center gap-2 cursor-pointer group shrink-0" aria-label="Planos home">
              <img src="/Planos Logo Red.png" alt="Planos Studio" className="h-11 sm:h-14 w-auto max-w-[200px] sm:max-w-[260px] object-contain object-left group-hover:opacity-90 transition-all" />
            </a>
            <div className="relative hidden md:block" ref={appsRef}>
              <button
                onClick={() => setAppsOpen((o) => !o)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/90 border border-slate-600/50 text-slate-200 hover:text-white hover:bg-slate-700/80 transition-all text-sm font-medium"
                aria-expanded={appsOpen}
                aria-haspopup="true"
              >
                <LayoutGrid size={16} />
                <span>Apps</span>
                <ChevronDown size={14} className={`transition-transform ${appsOpen ? 'rotate-180' : ''}`} />
              </button>
              {appsOpen && (
                <div className="absolute left-0 top-full mt-2 w-[340px] max-h-[80vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl z-50">
                  <div className="relative overflow-hidden rounded-2xl">
                    <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(168,85,247,0.18),transparent_55%),radial-gradient(120%_80%_at_100%_100%,rgba(56,189,248,0.12),transparent_55%)]" />
                    <div className="relative p-3 space-y-3">
                      {APP_GROUPS.map((group) => (
                        <div key={group.id}>
                          <div className={`px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider ${group.accent}`}>{group.label}</div>
                          <div className="grid grid-cols-2 gap-1.5">
                            {group.apps.map((app) => (
                              <a
                                key={app.id}
                                href={app.url}
                                onClick={(e) => { handleAppNavigation(app.id, app.url, e); setAppsOpen(false); }}
                                className={`flex items-center gap-2.5 px-2.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                  app.id === 'planos'
                                    ? `bg-gradient-to-br ${group.gradient} to-transparent text-white ring-1 ring-inset ring-white/15`
                                    : 'text-slate-300 hover:bg-slate-800/80 hover:text-white'
                                }`}
                              >
                                <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${group.iconBg}`}>
                                  <app.icon size={16} strokeWidth={1.8} />
                                </span>
                                <span className="truncate">{app.label}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto" ref={accountRef}>
            {user ? (
              <>
                {onNavigateToReferrals && (
                  <button
                    onClick={onNavigateToReferrals}
                    className="hidden sm:flex items-center gap-1.5 px-3 py-2 bg-emerald-600/20 border border-emerald-500/30 rounded-full text-emerald-300 hover:bg-emerald-600/30 hover:text-emerald-200 transition-all text-xs font-medium min-h-[36px]"
                  >
                    <Gift size={14} />
                    <span>Give 50, Get 50</span>
                  </button>
                )}
                {showBackToProjects && onBackToProjects && (
                  <button onClick={onBackToProjects} className="flex items-center gap-2 px-2.5 sm:px-3 py-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl text-sm font-medium transition-colors border border-slate-700 min-h-[40px] touch-manipulation" aria-label="Back to projects">
                    <FolderOpen size={18} /><span className="hidden sm:inline">Projects</span>
                  </button>
                )}
                <div className="relative" ref={creditsRef}>
                  {user.credits === 0 ? (
                    <button type="button" onClick={() => setCreditsPanelOpen((o) => !o)} className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 rounded-full border-2 border-red-400 transition-all group shadow-lg shadow-red-500/40 ring-2 ring-red-400/30 animate-pulse hover:animate-none min-h-[40px]">
                      <div className="w-6 h-6 rounded-full bg-red-700 text-white flex items-center justify-center border border-red-400 group-hover:border-red-300"><Coins size={12} /></div>
                      <div className="flex flex-col items-start leading-none">
                        <span className="font-bold text-white text-xs">Top Up</span>
                        <span className="text-[10px] text-red-100 font-medium hidden sm:inline">0 left</span>
                      </div>
                    </button>
                  ) : (
                    <button type="button" onClick={() => setCreditsPanelOpen((o) => !o)} className="flex items-center gap-1.5 px-2.5 py-2 pr-3 shrink-0 bg-red-600 hover:bg-red-500 rounded-full border-2 border-red-400 transition-all group shadow-lg shadow-red-500/40 ring-2 ring-red-400/30 min-h-[40px]">
                      <div className="w-6 h-6 rounded-full bg-red-700 text-white flex items-center justify-center border border-red-400 group-hover:border-red-300"><LayoutDashboard size={12} /></div>
                      <div className="flex flex-col items-start leading-none">
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-white text-sm sm:text-base">{formatCredits(user.credits)}</span>
                          <Coins size={12} className="text-white" />
                        </div>
                        <span className="text-[10px] text-red-100 font-semibold hidden sm:block">Credits</span>
                      </div>
                    </button>
                  )}
                  {creditsPanelOpen && (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={() => setCreditsPanelOpen(false)}>
                      <div className="bg-slate-900 border border-slate-600 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 relative">
                          <button type="button" onClick={() => setCreditsPanelOpen(false)} className="absolute top-4 right-4 p-1.5 hover:bg-slate-800 rounded-lg transition-colors">
                            <X size={18} className="text-slate-400" />
                          </button>
                          <div className="p-4 mb-4 rounded-xl border border-slate-700 bg-gradient-to-b from-red-950/40 to-transparent">
                            <div className="flex items-center gap-2 mb-2">
                              <a href="https://hub.rehub.studio" target="_blank" rel="noopener noreferrer" className="shrink-0">
                                <img src="/ReHub%20Logo%20Transparent%20White%20Text%20.png" alt="ReHub Studio" className="h-12 w-auto object-contain" />
                              </a>
                              <h3 className="font-bold text-white text-base">ReHub Credits</h3>
                            </div>
                            <p className="text-xs font-semibold text-red-300/90 uppercase tracking-wide mb-1">Five apps in one · One balance</p>
                            <p className="text-slate-300 text-sm mb-3">
                              Your balance: <span className="font-semibold text-red-200">{formatCredits(user.credits)}</span> · Use across PhotoClear, Staging, Sketchplan, 360 Tours & Planos
                            </p>
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Link2 size={12} /> Buy once, use anywhere
                            </p>
                          </div>
                          <div className="p-4 space-y-4 border-b border-slate-700/80">
                            <div className="flex gap-3 items-start">
                              <img src={LOGO_360_URL} alt="360 Tours" className="h-14 w-auto shrink-0 object-contain" />
                              <div>
                                <p className="text-xs font-semibold text-orange-200/90 mb-0.5">360 Tours</p>
                                <p className="text-sm text-slate-400 leading-snug">Create, host and share immersive 360° virtual tours. Embed on any site, fast and mobile-friendly.</p>
                              </div>
                            </div>
                            <div className="flex gap-3 items-start">
                              <img src={LOGO_PLANOS_URL} alt="Planos" className="h-12 w-auto shrink-0 object-contain" />
                              <div>
                                <p className="text-xs font-semibold text-red-200/90 mb-0.5">Planos</p>
                                <p className="text-sm text-slate-400 leading-snug">Overlay floor plans on Google Maps, add camera hotspots with real photos, and publish interactive spatial maps.</p>
                              </div>
                            </div>
                          </div>
                          <div className="p-4 space-y-3">
                            <div>
                              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Benefits</p>
                              <ul className="space-y-1.5 text-sm text-slate-400">
                                <li className="flex items-center gap-2"><Check size={14} className="text-red-500 shrink-0" /> ReHub Credits per month on plans</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-red-500 shrink-0" /> Same balance across all five apps</li>
                                <li className="flex items-center gap-2"><Check size={14} className="text-red-500 shrink-0" /> {CREDIT_EXPIRY_COPY.benefitPill}</li>
                              </ul>
                            </div>
                            {onOpenPlans && (
                              <div className="pt-2 pb-1 border-t border-slate-700/80">
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Subscription &amp; active spaces</p>
                                <p className="text-sm text-slate-400 mb-3">
                                  Pro, Agency, and Business plans give you <strong className="text-slate-300">active spaces</strong> for 360 Tours and Planos — publish without spending credits per space. Active spaces are shared across both apps.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => { setCreditsPanelOpen(false); onOpenPlans(); }}
                                  className="w-full py-2 rounded-lg border border-red-500/60 text-red-300 hover:bg-red-950/40 hover:text-red-200 font-medium text-sm transition-colors"
                                >
                                  View active space plans
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="pt-3">
                            <button
                              type="button"
                              onClick={() => { setCreditsPanelOpen(false); onOpenCredits?.(); }}
                              className="w-full py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold text-sm transition-colors"
                            >
                              {user.credits === 0 ? 'Get credits' : 'Buy more credits'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button onClick={() => setAccountOpen((o) => !o)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-slate-800 transition-colors text-sm font-medium min-h-[44px]" aria-expanded={accountOpen} aria-haspopup="true">
                    <span className="max-w-[120px] truncate hidden sm:inline">{user.email || 'Account'}</span>
                    <ChevronDown size={16} className={`transition-transform ${accountOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {accountOpen && (
                    <div className="absolute right-0 top-full mt-1 py-1 min-w-[180px] bg-slate-900 border border-slate-700 rounded-xl shadow-xl z-50">
                      {onOpenPlans && (
                        <button onClick={() => { onOpenPlans(); setAccountOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm">
                          <Zap size={16} />
                          Plans (active spaces)
                        </button>
                      )}
                      {onNavigateToSettings && <button onClick={() => { onNavigateToSettings(); setAccountOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"><Settings size={16} />Settings</button>}
                      {onNavigateToMembers && <button onClick={() => { onNavigateToMembers(); setAccountOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"><Users size={16} />Members</button>}
                      {onNavigateToReferrals && <button onClick={() => { onNavigateToReferrals(); setAccountOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"><Gift size={16} />Give 50, Get 50</button>}
                      {isAdmin && onOpenAdmin && <button onClick={() => { onOpenAdmin(); setAccountOpen(false); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"><Shield size={16} />Admin</button>}
                      <button
                        onClick={handleBillingPortal}
                        disabled={billingLoading}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm disabled:opacity-60"
                      >
                        {billingLoading ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                        Billing &amp; invoices
                      </button>
                      {billingNotice && (
                        <div className="px-4 py-2 text-xs text-amber-300/90 border-t border-slate-800">{billingNotice}</div>
                      )}
                      <button onClick={async () => { setAccountOpen(false); await supabase.auth.signOut(); onLogout(); }} className="w-full flex items-center gap-2 px-4 py-2.5 text-left text-slate-200 hover:bg-slate-800 rounded-lg text-sm"><LogOut size={16} />Logout</button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-1.5 sm:gap-3">
                <button onClick={onLogin} className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl font-medium transition-all text-sm touch-manipulation">
                  <LogIn size={18} />
                  <span className="hidden sm:inline">Log In</span>
                </button>
                <button onClick={onSignUp ?? onLogin} className="flex items-center justify-center gap-1.5 px-4 sm:px-5 py-2.5 min-h-[44px] bg-red-600 hover:bg-red-500 text-white rounded-xl font-semibold transition-all shadow-lg shadow-red-500/30 text-sm touch-manipulation">
                  <UserPlus size={18} />
                  <span>Sign Up</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="md:hidden border-t border-slate-800 py-2.5 flex items-center justify-center gap-3 overflow-x-auto shrink-0">
        <div className="flex items-center gap-1.5" aria-label="Switch app">
          {APPS.map((app) => (
            <a key={app.id} href={app.url} onClick={(e) => handleAppNavigation(app.id, app.url, e)} className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all touch-manipulation ${app.id === 'planos' ? 'bg-red-600 border-red-500 text-white' : 'border-slate-600 text-slate-400 hover:border-slate-500 hover:text-slate-300 active:bg-slate-800'}`} aria-label={`Switch to ${app.label}`}>
              <app.icon size={18} strokeWidth={2} />
            </a>
          ))}
        </div>
      </div>
    </header>
  );
};

/* ─── App ────────────────────────────────────────────── */
function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPricingBenefitsModal, setShowPricingBenefitsModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'signup'>('signup');
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const [currentProject, setCurrentProject] = useState<PlanosProject | null>(null);
  const [projects, setProjects] = useState<PlanosProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [callbackStatus, setCallbackStatus] = useState<string | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showSubscriptionPlansModal, setShowSubscriptionPlansModal] = useState(false);
  const [showReferrals, setShowReferrals] = useState(false);
  const [activeSpacesCheck, setActiveSpacesCheck] = useState<{ activeCount: number; limit: number } | null>(null);
  const callbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const viewMatch = currentPath.match(/^\/view\/(.+)$/);
  const embedMatch = currentPath.match(/^\/embed\/(.+)$/);
  const normalizeSlug = (value: string) => {
    const trimmed = value.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
    return trimmed.split('/')[0] || trimmed;
  };

  // ── Auth init (same pattern as photoclear/staging: let Supabase handle code exchange via getSession) ─────────────────
  useEffect(() => {
    if (!isConfigured) return;

    const isAuthCallback = window.location.pathname === '/auth/callback';

    // SSO tokens from other apps (cross-app navigation)
    const hash = window.location.hash;
    if (hash.includes('sso_access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      const at = params.get('sso_access_token');
      const rt = params.get('sso_refresh_token');
      window.history.replaceState({}, '', window.location.pathname);
      if (at && rt) {
        supabase.auth.setSession({ access_token: at, refresh_token: rt }).then(({ data }) => {
          if (data.session) setSession(data.session);
        });
        return;
      }
    }

    // getSession() triggers Supabase's internal URL detection and PKCE code exchange when on /auth/callback?code=...
    // (detectSessionInUrl: true). No manual exchangeCodeForSession — avoids PKCE verifier race.
    supabase.auth.getSession().then(({ data: { session: s }, error }) => {
      if (error && isAuthCallback) {
        console.error('[Planos] Auth session error:', error.message);
        setAuthError(error.message);
        setCallbackStatus(null);
      } else if (s) {
        setSession(s);
        if (isAuthCallback) {
          window.history.replaceState({}, '', '/');
          setCurrentPath('/');
          setCallbackStatus(null);
        }
      } else if (isAuthCallback) {
        setCallbackStatus('Completing sign-in...');
        callbackTimeoutRef.current = setTimeout(() => {
          setAuthError('Sign-in could not be completed. If you opened the link in a different browser or tab, try again in the same browser.');
          setCallbackStatus(null);
        }, 5000);
      }
    });

    return () => {
      if (callbackTimeoutRef.current) clearTimeout(callbackTimeoutRef.current);
    };
  }, []);

  // ── Listen for auth changes (redirect when session arrives on callback) ──────────────────────────
  useEffect(() => {
    if (!isConfigured) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      console.log('[Planos] Auth change:', _event);
      setSession(s);
      if (s) {
        // Apply referral code if present
        const pendingRef = localStorage.getItem('referral_code');
        if (pendingRef) {
          localStorage.removeItem('referral_code');
          const provider = s.user?.app_metadata?.provider;
          const authMethod =
            provider === 'google' ? 'google' : provider === 'email' ? 'magic_link' : null;
          void supabase
            .rpc('apply_referral_code', {
              p_code: pendingRef,
              p_auth_method: authMethod,
              p_app_source: 'planos-studio',
            })
            .catch(() => {});
        }
        if (window.location.pathname === '/auth/callback') {
          if (callbackTimeoutRef.current) clearTimeout(callbackTimeoutRef.current);
          window.history.replaceState({}, '', '/');
          setCurrentPath('/');
          setCallbackStatus(null);
          setAuthError(null);
        }
      }
      if (!s) { setUserProfile(null); setCurrentProject(null); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load profile when session appears ────────────────
  useEffect(() => {
    if (!session?.user?.id) return;
    supabase.from('profiles').select('*').eq('id', session.user.id).single().then(({ data }) => {
      setUserProfile(data ?? { id: session.user.id, email: session.user.email ?? '', credits: 0 });
    });
  }, [session?.user?.id]);

  // ── Active spaces (360 Tours + Planos) for dashboard summary ────────────────
  useEffect(() => {
    if (!userProfile?.id) {
      setActiveSpacesCheck(null);
      return;
    }
    canUserPublish(userProfile.id, userProfile).then((r) => {
      setActiveSpacesCheck(r.limit > 0 ? { activeCount: r.activeCount, limit: r.limit } : null);
    });
  }, [userProfile?.id, userProfile?.tours_subscription_tier]);

  // ── Load projects when profile appears ────────────────
  useEffect(() => {
    if (!session?.user?.id || !isConfigured) return;
    setProjectsLoading(true);
    getProjects(session.user.id).then((list) => {
      setProjects(list);
      setProjectsLoading(false);
    });
  }, [session?.user?.id]);

  const handleCreateProject = async (viewMode: ViewMode) => {
    if (!session?.user?.id) return;
    const created = await createProject(session.user.id, 'Untitled Plan', viewMode);
    if (created) {
      setCurrentProject(created);
      setProjects((prev) => [created, ...prev]);
    }
  };

  const handleBackToProjects = () => {
    setCurrentProject(null);
    if (session?.user?.id) getProjects(session.user.id).then(setProjects);
  };

  const handleOpenProject = (p: PlanosProject) => setCurrentProject(p);

  // Popstate
  useEffect(() => {
    const onPop = () => setCurrentPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const handleLogout = async () => { await supabase.auth.signOut(); setSession(null); setUserProfile(null); setCurrentProject(null); setProjects([]); };

  // Public viewer: return after all hooks so popstate/auth run
  if (viewMatch) return <PlanosViewer slugOrId={normalizeSlug(viewMatch[1])} />;
  if (embedMatch) return <PlanosViewer slugOrId={normalizeSlug(embedMatch[1])} isEmbed />;

  /* ─── Auth callback page ───────────────────────────── */
  if (currentPath === '/auth/callback') {
    const isPkceError = authError?.toLowerCase().includes('pkce') ?? false;
    return (
      <div className="flex items-center justify-center min-h-screen flex-col gap-4 px-4" style={{ backgroundColor: '#1a1a1a' }}>
        {callbackStatus && !authError && <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#FF3B30' }} />}
        <p className="text-slate-300 text-sm text-center">{callbackStatus || 'Processing...'}</p>
        {authError && (
          <div className="mt-4 max-w-md text-center space-y-4">
            <p className="text-red-400 text-sm">{authError}</p>
            {isPkceError && (
              <p className="text-slate-400 text-sm">
                Sign-in couldn&apos;t be completed because the verification step was lost. This usually happens if you opened the login link in a different browser or tab, or if site data was cleared. Please try again in the same browser without clearing data.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a href="/" className="px-6 py-2 rounded-xl text-white text-sm font-semibold" style={{ backgroundColor: '#FF3B30' }}>Back to home</a>
              <a href="/" className="px-6 py-2 rounded-xl text-sm font-medium border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 transition-colors">Try again</a>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─── Logged in: Dashboard or Editor ───────────────── */
  if (session && userProfile) {
    if (projectsLoading) {
      return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#1a1a1a' }}>
          <Header user={userProfile} onLogin={() => {}} onLogout={handleLogout} onOpenCredits={() => setShowCreditsModal(true)} onOpenPlans={() => setShowSubscriptionPlansModal(true)} onNavigateToReferrals={() => setShowReferrals(true)} />
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-3 text-slate-400">
              <Loader2 className="animate-spin" style={{ color: '#FF3B30' }} />
              <span>Loading...</span>
            </div>
          </div>
          {showCreditsModal && userProfile && (
            <CreditsModal user={userProfile} onClose={() => setShowCreditsModal(false)} onOpenPlans={() => { setShowCreditsModal(false); setShowSubscriptionPlansModal(true); }} />
          )}
          {showSubscriptionPlansModal && userProfile && (
            <SubscriptionPlansModal user={userProfile} onClose={() => setShowSubscriptionPlansModal(false)} />
          )}
          {showReferrals && userProfile && (
            <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
          )}
        </div>
      );
    }
    if (!currentProject) {
      return (
        <>
          <Dashboard
            projects={projects}
            onOpenProject={handleOpenProject}
            onCreateProject={handleCreateProject}
            onDeleteProject={async (p) => {
              const ok = await deleteProject(p.id);
              if (ok) setProjects((prev) => prev.filter((x) => x.id !== p.id));
              return ok;
            }}
            header={<Header user={userProfile} onLogin={() => {}} onLogout={handleLogout} onOpenCredits={() => setShowCreditsModal(true)} onOpenPlans={() => setShowSubscriptionPlansModal(true)} onNavigateToReferrals={() => setShowReferrals(true)} />}
            activeSpaces={activeSpacesCheck}
          />
          {showCreditsModal && userProfile && (
            <CreditsModal user={userProfile} onClose={() => setShowCreditsModal(false)} onOpenPlans={() => { setShowCreditsModal(false); setShowSubscriptionPlansModal(true); }} />
          )}
          {showSubscriptionPlansModal && userProfile && (
            <SubscriptionPlansModal user={userProfile} onClose={() => setShowSubscriptionPlansModal(false)} />
          )}
          {showReferrals && userProfile && (
            <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
          )}
          <ReferralPromoPopup
            client={supabase}
            userId={userProfile.id}
            appSource="planos"
            onInvite={() => setShowReferrals(true)}
            suppressed={showReferrals || showCreditsModal || showSubscriptionPlansModal}
          />
        </>
      );
    }
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#1a1a1a' }}>
        <Header user={userProfile} onLogin={() => {}} onLogout={handleLogout} showBackToProjects onBackToProjects={handleBackToProjects} onOpenCredits={() => setShowCreditsModal(true)} onOpenPlans={() => setShowSubscriptionPlansModal(true)} onNavigateToReferrals={() => setShowReferrals(true)} />
        <PlanosEditor
          project={currentProject}
          user={userProfile}
          onProjectUpdate={(p) => {
            setCurrentProject(p);
            if (session?.user?.id) getProjects(session.user.id).then(setProjects);
          }}
          onCreditsChange={(delta) => setUserProfile((prev) => prev ? { ...prev, credits: prev.credits + delta } : prev)}
          onProjectDelete={() => {
            if (currentProject) {
              setProjects((prev) => prev.filter((p) => p.id !== currentProject.id));
              setCurrentProject(null);
            }
          }}
          onOpenCredits={() => setShowCreditsModal(true)}
          onOpenPlans={() => setShowSubscriptionPlansModal(true)}
        />
        {showCreditsModal && userProfile && (
          <CreditsModal user={userProfile} onClose={() => setShowCreditsModal(false)} onOpenPlans={() => { setShowCreditsModal(false); setShowSubscriptionPlansModal(true); }} />
        )}
        {showSubscriptionPlansModal && userProfile && (
          <SubscriptionPlansModal user={userProfile} onClose={() => setShowSubscriptionPlansModal(false)} />
        )}
        {showReferrals && userProfile && (
          <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
        )}
      </div>
    );
  }

  /* ─── Landing page (same structure as staging/tours: scrollable wrapper, Header inside) ─────────────────────────────────── */
  return (
    <div className="min-h-screen flex flex-col bg-black relative overflow-hidden">
      <FluidBackground />
      <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-y-auto">
        <Header user={null} onLogin={() => { setLoginModalMode('login'); setShowLoginModal(true); }} onSignUp={() => { setLoginModalMode('signup'); setShowLoginModal(true); }} onLogout={() => {}} />
        <main className="relative z-10">
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <img src="/Planos Logo Red.png" alt="Planos.studio" className="h-28 md:h-36 w-auto object-contain drop-shadow-lg" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">Interactive Floor Plans &amp; Google Map Overlays</h1>
              <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">{PLANOS_TAGLINE}</p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button onClick={() => { setLoginModalMode('signup'); setShowLoginModal(true); }} className="group flex items-center gap-3 px-10 py-5 text-white rounded-2xl font-semibold text-lg transition-all hover:opacity-90 shadow-lg" style={{ backgroundColor: '#FF3B30', boxShadow: '0 10px 40px -10px rgba(255,59,48,0.4)' }}>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 group-hover:rotate-[360deg] transition-transform duration-700">
                    <Sparkles size={18} strokeWidth={2} />
                  </span>
                  Get Started Free
                </button>
                <button onClick={() => { setLoginModalMode('login'); setShowLoginModal(true); }} className="px-8 py-5 bg-slate-900/80 backdrop-blur-sm border-2 border-slate-500 hover:border-slate-400 text-slate-200 hover:text-white rounded-2xl font-semibold text-lg transition-all shadow-lg">
                  Sign In
                </button>
                <button
                  onClick={() => setShowPricingBenefitsModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-5 bg-teal-600 hover:bg-teal-500 text-white rounded-2xl font-medium text-base transition-all shadow-lg"
                >
                  <Tag size={18} />
                  Pricing & benefits
                </button>
              </div>
              <p className="text-sm text-slate-500 mt-4">10 free credits on signup. No credit card required.</p>

              <div className="mt-12 w-full max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl shadow-red-900/20">
                <div className="relative w-full pt-[56.25%] bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/6GPMGY4jFNI?rel=0"
                    title="Planos Studio"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16 text-left max-w-4xl mx-auto">
              {PLANOS_FEATURES.map((f) => (
                <div key={f.title} className="p-6 rounded-2xl border border-slate-700/80 bg-slate-900/70 backdrop-blur-md transition-colors hover:border-[#8E0000]/50">
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-400">{f.description}</p>
                </div>
              ))}
            </div>

            <div className="mt-16 max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-white text-center mb-6">See it in action</h2>
              <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: '12px', overflow: 'hidden' }}>
                <iframe
                  src="https://planos.rehub.studio/embed/p-0f6ff1ca"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </main>
        <div className="shrink-0">
          <Footer />
        </div>
        {showPricingBenefitsModal && (
          <PricingBenefitsModal
            onClose={() => setShowPricingBenefitsModal(false)}
            onSignUp={() => { setShowPricingBenefitsModal(false); setLoginModalMode('signup'); setShowLoginModal(true); }}
          />
        )}
        {showLoginModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="w-full max-w-lg my-auto py-8">
              <Login onClose={() => setShowLoginModal(false)} initialMode={loginModalMode} />
            </div>
          </div>
        )}
      </div>

      {showCreditsModal && userProfile && (
        <CreditsModal user={userProfile} onClose={() => setShowCreditsModal(false)} onOpenPlans={() => { setShowCreditsModal(false); setShowSubscriptionPlansModal(true); }} />
      )}
      {showReferrals && userProfile && (
        <ReferralsPage userId={userProfile.id} onClose={() => setShowReferrals(false)} />
      )}
    </div>
  );
}

export default App;
